/**
 * Icon Registry
 *
 * Central registry of Lucide icons used throughout the application.
 * Icons MUST be explicitly imported to survive tree-shaking in production.
 *
 * Why this exists:
 * - Bundlers remove unused code (tree-shaking)
 * - Dynamic access like `LucideIcons[name]` doesn't work in production
 * - All icons we want to use must be explicitly imported
 *
 * To add a new icon:
 * 1. Import it from 'lucide-react'
 * 2. Add it to ICON_REGISTRY
 * 3. Optionally add to NAVIGATION_ICONS if it should appear in pickers
 */

import {
  // General
  Home,
  Menu,
  Search,
  Settings,
  User,
  Users,
  Bell,
  Mail,
  // Navigation
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  Link,
  Globe,
  Map,
  MapPin,
  // Content
  FileText,
  File,
  Folder,
  FolderOpen,
  FolderKanban,
  Image,
  Video,
  Music,
  BookOpen,
  Book,
  Newspaper,
  Archive,
  // Actions
  Plus,
  Edit,
  Trash2,
  Copy,
  Download,
  Upload,
  Share,
  Save,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Check,
  X,
  // Communication
  Phone,
  MessageSquare,
  MessagesSquare,
  Send,
  Inbox,
  AtSign,
  // Business
  Building2,
  Briefcase,
  Package,
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  DollarSign,
  BarChart,
  PieChart,
  TrendingUp,
  // Organization
  Calendar,
  CalendarDays,
  Clock,
  Timer,
  Target,
  Flag,
  Tag,
  Bookmark,
  Star,
  Heart,
  ThumbsUp,
  Award,
  Trophy,
  CheckSquare,
  // Technical
  Code,
  Terminal,
  Database,
  Server,
  Cloud,
  Cpu,
  Wifi,
  Shield,
  ShieldCheck,
  Key,
  Fingerprint,
  // Layout
  LayoutDashboard,
  LayoutGrid,
  Layers,
  Grid,
  List,
  Table,
  Columns,
  Rows,
  SidebarOpen,
  PanelLeft,
  // Misc
  Palette,
  Paintbrush,
  Camera,
  Mic,
  Headphones,
  Play,
  Pause,
  HelpCircle,
  Info,
  AlertCircle,
  AlertTriangle,
  Lightbulb,
  Zap,
  Rocket,
  Sparkles,
  Gift,
  Store,
  // Additional for app
  Moon,
  Sun,
  LogOut,
  ToggleRight,
  Webhook,
  RefreshCcw,
  Loader2,
  Bot,
  type LucideIcon,
} from 'lucide-react'

/**
 * Complete icon registry - all icons must be listed here to work in production
 */
export const ICON_REGISTRY: Record<string, LucideIcon> = {
  // General
  Home,
  Menu,
  Search,
  Settings,
  User,
  Users,
  Bell,
  Mail,
  // Navigation
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  Link,
  Globe,
  Map,
  MapPin,
  // Content
  FileText,
  File,
  Folder,
  FolderOpen,
  FolderKanban,
  Image,
  Video,
  Music,
  BookOpen,
  Book,
  Newspaper,
  Archive,
  // Actions
  Plus,
  Edit,
  Trash2,
  Copy,
  Download,
  Upload,
  Share,
  Save,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Check,
  X,
  // Communication
  Phone,
  MessageSquare,
  MessagesSquare,
  Send,
  Inbox,
  AtSign,
  // Business
  Building2,
  Briefcase,
  Package,
  ShoppingCart,
  ShoppingBag,
  CreditCard,
  DollarSign,
  BarChart,
  PieChart,
  TrendingUp,
  // Organization
  Calendar,
  CalendarDays,
  Clock,
  Timer,
  Target,
  Flag,
  Tag,
  Bookmark,
  Star,
  Heart,
  ThumbsUp,
  Award,
  Trophy,
  CheckSquare,
  // Technical
  Code,
  Terminal,
  Database,
  Server,
  Cloud,
  Cpu,
  Wifi,
  Shield,
  ShieldCheck,
  Key,
  Fingerprint,
  // Layout
  LayoutDashboard,
  LayoutGrid,
  Layers,
  Grid,
  List,
  Table,
  Columns,
  Rows,
  SidebarOpen,
  PanelLeft,
  // Misc
  Palette,
  Paintbrush,
  Camera,
  Mic,
  Headphones,
  Play,
  Pause,
  HelpCircle,
  Info,
  AlertCircle,
  AlertTriangle,
  Lightbulb,
  Zap,
  Rocket,
  Sparkles,
  Gift,
  Store,
  // Additional for app
  Moon,
  Sun,
  LogOut,
  ToggleRight,
  Webhook,
  RefreshCcw,
  Loader2,
  Bot,
}

/**
 * Icons shown in the icon picker (subset of registry)
 * Sorted by category for better UX
 */
export const NAVIGATION_ICONS = [
  // General
  'Home', 'Menu', 'Search', 'Settings', 'User', 'Users', 'Bell', 'Mail',
  // Navigation
  'ChevronRight', 'ChevronDown', 'ChevronLeft', 'ChevronUp', 'ArrowRight', 'ArrowLeft',
  'ExternalLink', 'Link', 'Globe', 'Map', 'MapPin',
  // Content
  'FileText', 'File', 'Folder', 'FolderOpen', 'FolderKanban', 'Image', 'Video', 'Music',
  'BookOpen', 'Book', 'Newspaper', 'Archive',
  // Actions
  'Plus', 'Edit', 'Trash2', 'Copy', 'Download', 'Upload', 'Share', 'Save',
  'Eye', 'EyeOff', 'Lock', 'Unlock', 'Check', 'X',
  // Communication
  'Phone', 'MessageSquare', 'MessagesSquare', 'Send', 'Inbox', 'AtSign',
  // Business
  'Building2', 'Briefcase', 'Package', 'ShoppingCart', 'ShoppingBag', 'CreditCard',
  'DollarSign', 'BarChart', 'PieChart', 'TrendingUp',
  // Organization
  'Calendar', 'CalendarDays', 'Clock', 'Timer', 'Target', 'Flag', 'Tag', 'Bookmark', 'Star',
  'Heart', 'ThumbsUp', 'Award', 'Trophy', 'CheckSquare',
  // Technical
  'Code', 'Terminal', 'Database', 'Server', 'Cloud', 'Cpu', 'Wifi',
  'Shield', 'ShieldCheck', 'Key', 'Fingerprint',
  // Layout
  'LayoutDashboard', 'LayoutGrid', 'Layers', 'Grid', 'List', 'Table',
  'Columns', 'Rows', 'SidebarOpen', 'PanelLeft',
  // Misc
  'Palette', 'Paintbrush', 'Camera', 'Mic', 'Headphones', 'Play', 'Pause',
  'HelpCircle', 'Info', 'AlertCircle', 'AlertTriangle', 'Lightbulb',
  'Zap', 'Rocket', 'Sparkles', 'Gift', 'Store', 'Bot',
] as const

/**
 * Get icon component by name
 * Returns null if icon not found in registry
 */
export function getIcon(name: string | undefined): LucideIcon | null {
  if (!name) return null
  return ICON_REGISTRY[name] || null
}

// Re-export the type for convenience
export type { LucideIcon }
